'use client'

import { useState, useEffect, useCallback } from 'react'
import { PaymentWithDetails } from '@/types'
import { useCurrency } from '@/lib/currency'
import PaymentReceipt from '@/components/client/PaymentReceipt'


interface PaymentManagementProps {
  landlordId: string
}

export default function PaymentManagement({ landlordId }: PaymentManagementProps) {
  const [payments, setPayments] = useState<PaymentWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed' | 'failed'>('all')
  const [totalRevenue, setTotalRevenue] = useState(0)
  const [, setPendingAmount] = useState(0) // Used in setPendingAmount
  const [totalCommission, setTotalCommission] = useState(0)
  const [netRevenue, setNetRevenue] = useState(0)
  const { format } = useCurrency()

    // Load payments for this landlord
  const loadPayments = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        console.error('No auth token found')
        setLoading(false)
        return
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const response = await fetch(`${apiUrl}/payments/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to load payments: ${response.statusText}`)
      }

      const data = await response.json()
      const paymentsArray = data.payments || []

      // Transform payments to match frontend format
      const transformedPayments: PaymentWithDetails[] = paymentsArray.map((payment: any) => ({
        id: payment._id || payment.id,
        client_id: payment.user?._id || payment.user || payment.client_id,
        landlord_id: payment.application?.landlord?._id || payment.application?.landlord || payment.landlord_id,
        property_id: payment.application?.property?._id || payment.application?.property || payment.property_id,
        application_id: payment.application?._id || payment.application || payment.application_id,
        amount: payment.amount || 0,
        currency: payment.currency || 'ngn',
        status: payment.status || 'pending',
        payment_method: payment.paymentMethod || 'stripe',
        description: payment.description || 'Rental Payment',
        transaction_id: payment.stripeChargeId || payment.stripePaymentIntentId,
        stripe_payment_intent_id: payment.stripePaymentIntentId,
        commission_amount: payment.commission_amount || 0,
        landlord_amount: payment.amount - (payment.commission_amount || 0),
        created_at: payment.createdAt || payment.created_at,
        updated_at: payment.updatedAt || payment.updated_at,
        // Include escrow information
        isEscrow: payment.isEscrow,
        escrowStatus: payment.escrowStatus,
        escrowHeldAt: payment.escrowHeldAt,
        escrowExpiresAt: payment.escrowExpiresAt,
        escrowReleasedAt: payment.escrowReleasedAt,
        propertyVisited: payment.propertyVisited,
        documentsReceived: payment.documentsReceived,
        application: payment.application ? {
          property: payment.application.property ? {
            title: payment.application.property.title,
            location: payment.application.property.address 
              ? `${payment.application.property.address.street}, ${payment.application.property.address.city}`
              : payment.application.property.location || 'N/A'
          } : undefined
        } : undefined
      }))

      setPayments(transformedPayments)

      // Calculate totals
      const completed = transformedPayments.filter(p => p.status === 'completed')
      const pending = transformedPayments.filter(p => p.status === 'pending')
      // const escrowHeld = transformedPayments.filter(p => p.isEscrow && (p as any).escrowStatus === 'held') // Unused

      // Total revenue for landlord = sum of (amount - commission) for released escrow payments
      // For non-escrow or held escrow payments, use full amount
      const totalRev = completed.reduce((sum, p) => {
        if (p.isEscrow && (p as any).escrowStatus === 'released' && p.commission_amount) {
          // Released escrow: landlord gets amount minus commission
          return sum + (p.amount - (p.commission_amount || 0))
        } else {
          // Non-escrow or held escrow: landlord gets full amount (commission not deducted yet)
          return sum + p.amount
        }
      }, 0)
      
      const totalComm = completed
        .filter(p => p.isEscrow && (p as any).escrowStatus === 'released' && p.commission_amount)
        .reduce((sum, p) => sum + (p.commission_amount || 0), 0)
      const netRev = totalRev // Net revenue is already calculated above (amount - commission)
      // const escrowAmount = escrowHeld.reduce((sum, p) => sum + p.amount, 0) // Unused

      setTotalRevenue(totalRev) // This is now net revenue (amount - commission)
      setPendingAmount(pending.reduce((sum, p) => sum + p.amount, 0))
      setTotalCommission(totalComm)
      setNetRevenue(netRev)
    } catch (error) {
      console.error('Error loading payments:', error)
    } finally {
      setLoading(false)
    }
  }, [landlordId])

  useEffect(() => {
    loadPayments()
  }, [landlordId, loadPayments])

  // Format currency via currency context
  const formatCurrency = (amount: number) => format(amount)

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      case 'refunded':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    if (filter === 'all') return true
    return payment.status === filter
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Payment Management</h2>
        <div className="flex space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'pending' | 'completed' | 'failed')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Payments</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Total Revenue</h3>
          <p className="text-3xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
          <p className="text-sm text-gray-500">From completed payments</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Net Revenue</h3>
          <p className="text-3xl font-bold text-blue-600">{formatCurrency(netRevenue)}</p>
          <p className="text-sm text-gray-500">After commission</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Commission Paid</h3>
          <p className="text-3xl font-bold text-red-600">{formatCurrency(totalCommission)}</p>
          <p className="text-sm text-gray-500">Platform fees</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">In Escrow</h3>
          <p className="text-3xl font-bold text-blue-600">{formatCurrency(
            payments.filter(p => p.isEscrow && (p as any).escrowStatus === 'held').reduce((sum, p) => sum + p.amount, 0)
          )}</p>
          <p className="text-sm text-gray-500">Held in escrow</p>
        </div>
      </div>

      {/* Payments List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Payment History</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading payments...</p>
          </div>
        ) : filteredPayments.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredPayments.map((payment) => (
              <div key={payment.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {payment.application?.property?.title || 'Unknown Property'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {payment.application?.property?.location || 'Unknown Location'}
                    </p>
                    <p className="text-sm text-gray-500">
                      Tenant: {(payment.application as any)?.client?.firstName || payment.client_id || 'N/A'}
                    </p>
                    {(payment as any).isEscrow && (
                      <div className="mt-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          (payment as any).escrowStatus === 'held' ? 'bg-blue-100 text-blue-800' :
                          (payment as any).escrowStatus === 'released' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {(payment as any).escrowStatus === 'held' ? '💰 Held in Escrow' :
                           (payment as any).escrowStatus === 'released' ? '✅ Released' :
                           'Escrow'}
                        </span>
                        {(payment as any).escrowStatus === 'held' && (
                          <p className="text-xs text-gray-500 mt-1">
                            Rent received; held in escrow. When released, platform commission will be deducted and the net amount will be added to your available balance.
                          </p>
                        )}
                      </div>
                    )}
                    <p className="text-sm text-gray-500">
                      Date: {new Date(payment.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(payment.amount)}
                    </p>
                    {payment.status === 'completed' && payment.commission_amount && (
                      <p className="text-sm text-red-600">
                        -{formatCurrency(payment.commission_amount)} commission
                      </p>
                    )}
                    {payment.status === 'completed' && payment.landlord_amount && (
                      <p className="text-sm text-blue-600 font-medium">
                        Net: {formatCurrency(payment.landlord_amount)}
                      </p>
                    )}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </span>
                  </div>
                </div>
                
                {payment.description && (
                  <p className="text-sm text-gray-600 mt-2">{payment.description}</p>
                )}
                
                {payment.transaction_id && (
                  <p className="text-xs text-gray-500 mt-1">
                    Transaction ID: {payment.transaction_id}
                  </p>
                )}
                
                {/* Receipt Button */}
                {payment.status === 'completed' && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <PaymentReceipt 
                      paymentId={payment.id}
                      buttonText="View Payment Receipt"
                      buttonClassName="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No payments found</p>
            <p className="text-sm">Payments will appear here when tenants make rent payments</p>
          </div>
        )}
      </div>

      {/* Payment Instructions */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-2">Payment Information</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>• Payments are processed automatically when tenants submit rent payments</p>
          <p>• Completed payments are transferred to your account within 2-3 business days</p>
          <p>• Failed payments will be retried automatically</p>
          <p>• Contact support if you have any payment-related questions</p>
        </div>
      </div>
    </div>
  )
} 


